import { Link } from "react-router-dom";
import type { NotificationResponse, OpsActivity, WarningItem } from "../api/client.js";
import { chicagoTime } from "../lib/format.js";
import { DetailLine, ExpandableRow } from "./PanelTail.js";

/**
 * One notification, and one warning, rendered the same way wherever they appear: the
 * operations panel and the dedicated pages both use these, so one record never has two
 * renderings that can drift.
 */

export function NotificationRow({
  notification: n,
  open,
  onToggle,
}: {
  notification: NotificationResponse;
  open: boolean;
  onToggle: () => void;
}) {
  const at = chicagoTime(n.createdAt);
  return (
    <ExpandableRow
      open={open}
      onToggle={onToggle}
      summary={
        <>
          <span className="fw" title={at?.full ?? n.createdAt}>
            {at?.short ?? "—"}
          </span>
          <span className="ft">{n.kind.replace(/_/g, " ")}</span>
          {/*
            Undelivered is a state worth showing: a webhook that stopped working shows up
            here before anyone notices the silence.
          */}
          <span className={`fd${n.deliveredAt ? "" : " unknown"}`}>
            {n.channelsDelivered.length > 0 ? n.channelsDelivered.join(", ") : "not sent"}
          </span>
          <span className="fm">{n.message.title}</span>
        </>
      }
      detail={
        <>
          <DetailLine k="raised" v={at?.full ?? n.createdAt} />
          {/*
            The text that went out, so "what did Mosaic tell me about this" has an
            answer.
          */}
          <DetailLine k="message" v={<span className="dlbody">{n.message.body}</span>} />
          <DetailLine k="severity" v={n.message.severity ?? "info"} />
          <DetailLine
            k="delivered"
            v={
              n.deliveredAt
                ? `${n.channelsDelivered.join(", ")} · ${chicagoTime(n.deliveredAt)?.full ?? n.deliveredAt}`
                : "not sent — no channel has accepted it"
            }
          />
          {n.subjectType === "situation" && n.subjectId && (
            <DetailLine
              k="situation"
              v={<Link to={`/situations/${n.subjectId}`}>open the situation</Link>}
            />
          )}
          {n.message.url && (
            <DetailLine
              k="link"
              v={
                <a href={n.message.url} target="_blank" rel="noreferrer">
                  {n.message.url}
                </a>
              }
            />
          )}
        </>
      }
    />
  );
}

/**
 * One recorded warning as an expandable row: when, scope, ticker, subject and message, and
 * when opened, the full detail.
 */
export function WarningRow({
  warning: w,
  open,
  onToggle,
}: {
  warning: WarningItem;
  open: boolean;
  onToggle: () => void;
}) {
  /**
   * When, in the owner's own timezone: a warning list is opened to ask whether this is
   * happening now or left over from an earlier run.
   */
  const at = chicagoTime(w.createdAt);
  return (
    <ExpandableRow
      open={open}
      onToggle={onToggle}
      summary={
        <>
          <span className="fw" title={at?.full ?? w.createdAt}>
            {at?.short ?? "—"}
          </span>
          <span className="ft">{w.scope}</span>
          {/*
            The ticker is the shortest thing that makes a row recognisable; the subject
            beside it (an accession, a CIK, a bare UUID) is not.
          */}
          {w.ticker && <span className="fk">{w.ticker}</span>}
          <span className="fd">{w.subjectId ?? "—"}</span>
          <span className="fm">{w.message}</span>
        </>
      }
      detail={
        <>
          <DetailLine k="recorded" v={at?.full ?? w.createdAt} />
          <DetailLine k="scope" v={w.scope} />
          {/*
            Whose filing this was, resolved from the subject: `financials` and `embed`
            warnings carry a CIK and a bare UUID, which name nothing to a person.
          */}
          <DetailLine k="company" v={w.company ?? "not a company's filing"} />
          <DetailLine k="ticker" v={w.ticker ?? "none listed"} />
          {/*
            The document being processed, where the subject names one: on an `embed` or
            `extract_text` warning, which document failed decides whether it matters. The
            type is shown because a filename alone does not say what the document is.
          */}
          {w.document && (
            <DetailLine
              k="file"
              v={
                <>
                  {w.document.filename}
                  {w.document.documentType ? ` · ${w.document.documentType}` : ""}
                  <span className="dlnote"> · of {w.document.accession}</span>
                </>
              }
            />
          )}
          <DetailLine
            k="situation"
            v={
              w.situation ? (
                <>
                  <Link to={`/situations/${w.situation.id}`}>{w.situation.name}</Link>
                  {w.situation.alsoOn > 0 && (
                    <span className="dlnote">
                      {" "}
                      · this filing is also on {w.situation.alsoOn} other situation
                      {w.situation.alsoOn === 1 ? "" : "s"}
                    </span>
                  )}
                </>
              ) : (
                "none — this filing is on no situation"
              )
            }
          />
          <DetailLine k="subject" v={w.subjectId ?? "none"} />
          {/*
            The message in full: the row above truncates it, and the part a tolerant parser
            could not read is often past the truncation.
          */}
          <DetailLine k="message" v={<span className="dlbody">{w.message}</span>} />
        </>
      }
    />
  );
}

/**
 * One job the worker is running right now, openable to say whose filing it is, which
 * situation it moves, and whether it has stalled.
 *
 * **The lease line matters most.** `leaseSeconds` says when a worker last renewed its claim
 * and `runningSeconds` how long the job has been going; together they separate a worker
 * working from one wedged on a document from one that died holding the claim. The lease is
 * stated in words because "lease lapsed" alone reads as a problem when it often clears
 * itself.
 */
export function RunningJobRow({
  job,
  open,
  onToggle,
  leaseNote,
  elapsed,
}: {
  job: OpsActivity["running"][number];
  open: boolean;
  onToggle: () => void;
  /** The page's own sentence about this claim's lease, which knows the agent rule. */
  leaseNote: string;
  elapsed: string;
}) {
  const started = job.startedAt ? chicagoTime(job.startedAt) : null;
  return (
    <ExpandableRow
      open={open}
      onToggle={onToggle}
      summary={
        <>
          <span className="ft">{job.kind}</span>
          {job.ticker && <span className="fk">{job.ticker}</span>}
          <span className="fd">{job.subject ?? "no subject"}</span>
          {job.situation && <span className="fsit">{job.situation.name}</span>}
          <span className="fx">
            {/*
              `embed_document` writes a document's chunks in batches, so the count during a
              run is the progress. The total is an estimate, the chunker's expansion over
              the document's characters, and runs high on an 8-K whose cover page is
              trimmed before chunking.
            */}
            {job.progress
              ? `${job.progress.chunksWritten.toLocaleString()} of ~${
                  job.progress.estimatedChunks?.toLocaleString() ?? "?"
                } chunks`
              : (job.workerId ?? "unclaimed")}
          </span>
          <span className="fn">
            {job.runningSeconds === null ? "start not recorded" : `running ${elapsed}`}
          </span>
        </>
      }
      detail={
        <>
          <DetailLine
            k="started"
            v={started ? started.full : "not recorded — claimed before this was timed"}
          />
          <DetailLine k="running for" v={elapsed} />
          {/*
            The two clocks, kept apart: `started_at` is written once at claim; `locked_at`
            is renewed by a live worker.
          */}
          <DetailLine k="lease" v={leaseNote} />
          <DetailLine k="worker" v={job.workerId ?? "unclaimed"} />
          <DetailLine
            k="attempt"
            v={
              job.attempt > 1
                ? `${job.attempt} of ${job.maxAttempts} — this has been tried before`
                : `1 of ${job.maxAttempts}`
            }
          />
          {job.progress && (
            <DetailLine
              k="progress"
              v={
                <>
                  {job.progress.chunksWritten.toLocaleString()} chunks written of roughly{" "}
                  {job.progress.estimatedChunks?.toLocaleString() ?? "an unknown number"}
                  <span className="dlnote">
                    {" "}
                    · the estimate is the chunker's expansion factor over the document's
                    characters, not a count — it runs high on a trimmed 8-K
                  </span>
                </>
              }
            />
          )}
          <DetailLine k="company" v={job.company ?? "not a company's filing"} />
          <DetailLine k="ticker" v={job.ticker ?? "none listed"} />
          {job.document && (
            <DetailLine
              k="file"
              v={
                <>
                  {job.document.filename}
                  {job.document.documentType ? ` · ${job.document.documentType}` : ""}
                  <span className="dlnote"> · of {job.document.accession}</span>
                </>
              }
            />
          )}
          <DetailLine
            k="situation"
            v={
              job.situation ? (
                <Link to={`/situations/${job.situation.id}`}>{job.situation.name}</Link>
              ) : (
                "none — this run is not about a situation"
              )
            }
          />
          <DetailLine k="subject" v={job.subject ?? "none"} />
        </>
      }
    />
  );
}
